/* ═══════════════════════════════════════════════════════════
   IG Ingredients — runtime for the ingredient library
   ───────────────────────────────────────────────────────────
   Drives one ig-ingredients-header against any number of
   ig-ingredient-group sections after it. They are separate
   Shopify sections, so everything is wired through the DOM.

   Three jobs:
     1. Tabs pick one product; the rest are removed from view.
     2. A row opens; its siblings close.
     3. On the split layout the open panel is MOVED into the pane
        on the right. Moved, not copied — there is one of each
        panel in the document at any time, so nothing is
        duplicated for search engines or screen readers.

   Nothing navigates and nothing is replaced, so there is never
   anywhere to come back from. The only history touched is
   replaceState on the hash, so an open ingredient stays linkable
   without stacking entries behind the back button.
   ═══════════════════════════════════════════════════════════ */

(function () {
  var SPLIT_AT = 750;

  function boot() {
    var groups = Array.prototype.slice.call(document.querySelectorAll('[data-ig-ing-group]'));
    if (!groups.length) return;

    var header = document.querySelector('[data-ig-ing-header]');
    var tabsHost = header && header.querySelector('[data-ig-ing-tabs]');
    var bar = header && header.querySelector('[data-ig-ing-bar]');

    function isSplit() {
      try { return window.matchMedia('(min-width: ' + SPLIT_AT + 'px)').matches; }
      catch (e) { return false; }
    }

    /* The sticky list has to clear the sticky bar above it, and the
       bar's height depends on how the key wraps. Measured rather
       than guessed, and re-measured on resize. */
    function measureBar() {
      if (!bar) return;
      var h = Math.round(bar.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--ig-ing-bar-h', h + 'px');
    }

    /* ───── tabs ───── */
    function buildTabs() {
      if (!tabsHost) return;
      tabsHost.innerHTML = '';
      groups.forEach(function (g, i) {
        var slug = g.getAttribute('data-slug');
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ig-ing__tab';
        b.setAttribute('role', 'tab');
        b.setAttribute('data-ig-ing-tab', slug);
        b.setAttribute('aria-selected', String(i === 0));
        // Wrapped so igni-italic.js can split the wordmark inside a
        // single child rather than across the button's own spacing.
        var t = document.createElement('span');
        t.className = 'ig-ing__tab-label';
        t.textContent = g.getAttribute('data-label') || slug;
        b.appendChild(t);
        tabsHost.appendChild(b);
      });
    }

    /* ───── panels ─────
       One place decides where every panel belongs, so the layout is
       correct after any change — open, close, tab switch, resize. */
    function placePanels(group) {
      if (!group) return;
      var pane = group.querySelector('[data-ig-ing-pane]');
      var split = isSplit();
      Array.prototype.forEach.call(group.querySelectorAll('.ig-ing__row'), function (row) {
        var key = row.getAttribute('data-ig-ing-key');
        var panel = group.querySelector('[data-ig-ing-panel="' + key + '"]');
        if (!panel) return;
        var wantsPane = split && pane && row.classList.contains('is-open');
        var target = wantsPane ? pane : row;
        if (panel.parentNode !== target) target.appendChild(panel);
      });
    }

    function ensureOpen(group) {
      if (!group || !isSplit()) return;
      if (group.querySelector('.ig-ing__row.is-open')) return;
      var first = group.querySelector('[data-ig-ing-row]');
      if (!first) return;
      first.closest('.ig-ing__row').classList.add('is-open');
      first.setAttribute('aria-expanded', 'true');
    }

    function selectGroup(slug, scroll) {
      groups.forEach(function (g) {
        var on = g.getAttribute('data-slug') === slug;
        g.hidden = !on;
        if (on) { ensureOpen(g); placePanels(g); }
      });
      if (tabsHost) {
        Array.prototype.forEach.call(tabsHost.children, function (t) {
          var on = t.getAttribute('data-ig-ing-tab') === slug;
          t.setAttribute('aria-selected', String(on));
          if (on && t.scrollIntoView) {
            try { t.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e) {}
          }
        });
      }
      if (scroll && header) {
        try { header.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (e) {}
      }
    }

    /* ───── rows ───── */
    function closeAllIn(scope, except) {
      Array.prototype.forEach.call(scope.querySelectorAll('.ig-ing__row'), function (r) {
        if (r === except) return;
        r.classList.remove('is-open');
        var head = r.querySelector('[data-ig-ing-row]');
        if (head) head.setAttribute('aria-expanded', 'false');
      });
    }

    function toggleRow(head) {
      var row = head.closest('.ig-ing__row');
      // Scoped to the product, not the list: a sub-group divider splits
      // the list in two, and one open row per product is the intent.
      var scope = head.closest('[data-ig-ing-group]');
      if (!row || !scope) return;
      var open = row.classList.contains('is-open');
      // Closing the only open row would leave the pane blank, so on the
      // split layout a second tap on the same row does nothing.
      if (open && isSplit()) return;

      closeAllIn(scope, row);
      row.classList.toggle('is-open', !open);
      head.setAttribute('aria-expanded', String(!open));
      placePanels(scope);

      if (!open) {
        var key = row.getAttribute('data-ig-ing-key');
        if (key && history.replaceState) history.replaceState(null, '', '#' + key);
        // A row near the fold would otherwise open off-screen. On the
        // split layout the list does not move, so nothing to chase.
        if (!isSplit()) {
          window.requestAnimationFrame(function () {
            var top = row.getBoundingClientRect().top;
            if (top < 60 || top > window.innerHeight * 0.6) {
              try { row.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (e) {}
            }
          });
        }
      }
    }

    function openKey(key) {
      var row = document.querySelector('[data-ig-ing-key="' + key + '"]');
      if (!row) return false;
      var g = row.closest('[data-ig-ing-group]');
      if (g) selectGroup(g.getAttribute('data-slug'), false);
      var head = row.querySelector('[data-ig-ing-row]');
      if (head) {
        closeAllIn(g || row.parentNode, row);
        row.classList.add('is-open');
        head.setAttribute('aria-expanded', 'true');
      }
      if (g) placePanels(g);
      window.requestAnimationFrame(function () {
        try { row.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (e) {}
      });
      return true;
    }

    /* ───── input ───── */
    document.addEventListener('click', function (ev) {
      var tab = ev.target.closest('[data-ig-ing-tab]');
      if (tab) { ev.preventDefault(); selectGroup(tab.getAttribute('data-ig-ing-tab'), true); return; }
      var head = ev.target.closest('[data-ig-ing-row]');
      if (head) { ev.preventDefault(); toggleRow(head); }
    });

    /* ───── boot ─────
       .is-enhanced is what collapses the panels. Until this runs
       every panel is open, so the copy and studies are readable and
       indexable with JavaScript off. */
    buildTabs();
    measureBar();
    Array.prototype.forEach.call(document.querySelectorAll('.ig-ing__list'), function (l) {
      l.classList.add('is-enhanced');
    });

    var hash = (location.hash || '').replace('#', '');
    if (!hash || !openKey(hash)) {
      selectGroup(groups[0].getAttribute('data-slug'), false);
    }

    // Crossing the breakpoint changes where panels live and how tall
    // the sticky bar is, so both are recomputed rather than assumed.
    var t = null;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        measureBar();
        groups.forEach(function (g) {
          if (g.hidden) return;
          ensureOpen(g);
          placePanels(g);
        });
      }, 150);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
